import { observer } from 'mobx-react-lite'
import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import Modal from '../../../../Modal/Modal'
import { InfluencerContext } from '../../../../../contexts/InfluencerContext'
import Authentication from '../../../../../models/Authentication'
import { formatNumber } from '../../../../../utils/Helper'
import { useParams } from 'react-router-dom'
import EChartsReact from 'echarts-for-react'
import PptxGenJS from 'pptxgenjs'
import { Instagram, Tiktok, Youtube } from 'react-bootstrap-icons'

export default observer(function SedcardModal({ isOpen, onClose }) {
  const { id } = useParams()
  const { authentications, influencer } = useContext(InfluencerContext)

  // Refs for individual elements
  const postsRef = useRef(null)
  const headerRef = useRef(null)
  const tableRef = useRef(null)
  const chartRef = useRef(null)

  const currentAuthentication = useMemo(() => authentications.find(authentication => +authentication.id === +id ) || new Authentication(), [id, authentications, authentications.length] )
  const posts = useMemo(() => (currentAuthentication.posts || []).slice(0, 4), [currentAuthentication.posts, currentAuthentication.posts?.length])

  // Local state for editable post tiles (so selection can replace an image)
  const [displayedPosts, setDisplayedPosts] = useState(posts)
  useEffect(() => {
    setDisplayedPosts(posts)
  }, [posts])

  // Carousel/lightbox state
  const [isCarouselOpen, setIsCarouselOpen] = useState(false)
  const [carouselIndex, setCarouselIndex] = useState(0)
  const [activeTileIndex, setActiveTileIndex] = useState(null)

  // Touch/swipe state
  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)

  const allPosts = useMemo(() => currentAuthentication.posts || [], [currentAuthentication.posts, currentAuthentication.posts?.length])

  const openCarouselForTile = useCallback((tileIdx) => {
    const currentTilePost = displayedPosts[tileIdx]
    const startIndex = Math.max(0, allPosts.findIndex(p => p?.id === currentTilePost?.id) )
    setActiveTileIndex(tileIdx)
    setCarouselIndex(startIndex === -1 ? 0 : startIndex)
    setIsCarouselOpen(true)
  }, [allPosts, displayedPosts])

  const closeCarousel = useCallback(() => {
    setIsCarouselOpen(false)
    setActiveTileIndex(null)
  }, [])

  const showPrev = useCallback(() => {
    if (!allPosts.length) return
    setCarouselIndex(prev => (prev - 1 + allPosts.length) % allPosts.length)
  }, [allPosts.length])

  const showNext = useCallback(() => {
    if (!allPosts.length) return
    setCarouselIndex(prev => (prev + 1) % allPosts.length)
  }, [allPosts.length])

  const selectCurrentImageForTile = useCallback(() => {
    if (activeTileIndex == null) return
    const selectedPost = allPosts[carouselIndex]
    setDisplayedPosts(prev => {
      const next = [...prev]
      next[activeTileIndex] = selectedPost
      return next
    })
    closeCarousel()
  }, [activeTileIndex, allPosts, carouselIndex, closeCarousel])

  // Touch handlers for swipe navigation
  const handleTouchStart = useCallback((e) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }, [])

  const handleTouchMove = useCallback((e) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }, [])

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe) {
      showNext()
    } else if (isRightSwipe) {
      showPrev()
    }
  }, [touchStart, touchEnd, showNext, showPrev])

  // Keyboard navigation for carousel
  useEffect(() => {
    if (!isCarouselOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeCarousel()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        showPrev()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        showNext()
      }
    }

    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
    }
  }, [isCarouselOpen, closeCarousel, showPrev, showNext])

  const currentMetrics = useMemo(() => {
    const auth = currentAuthentication
    // Első rendernél vagy adat hiányában üres értékek
    if (!auth || (!auth.id && auth.reach == null && auth.engagement_rate == null && !auth.audience_information)) {
      return { reach: '', engagement: '', german: '' }
    }

    const reach = formatNumber(auth.reach, 0) || ''
    const engagement = auth?.engagement_rate != null ? `${(+auth.engagement_rate * 100).toFixed(2)}%` : ''

    const german = (() => {
      // Próbáljuk több lehetséges mezőből és szerkezetből kinyerni az országokat
      const countriesRaw =
        auth?.audience_information?.detailed_audience_stats?.country ||
        auth?.audience_information?.detailed_audience_stats?.countries ||
        auth?.audience_information?.countries ||
        []

      const countries = Array.isArray(countriesRaw)
        ? countriesRaw
        : (countriesRaw && typeof countriesRaw === 'object'
          ? Object.values(countriesRaw)
          : [])

      if (!countries || countries.length === 0) return ''

      const de = countries.find(c => {
        const code = (c.country_code || c.code || '').toString().toUpperCase()
        const name = (c.name || '').toString().toLowerCase()
        return code === 'DE' || name.includes('german') || name === 'deutschland'
      })

      if (!de) return ''

      const percentage = (de.percentage ?? de.percent ?? de.value)
      if (percentage == null) return ''

      const pct = percentage > 1 ? percentage : percentage * 100
      return `${Number.isFinite(pct) ? pct.toFixed(2) : '0.00'}%`
    })()

    return { reach, engagement, german }
  }, [
    currentAuthentication?.id,
    currentAuthentication?.reach,
    currentAuthentication?.engagement_rate,
    currentAuthentication?.audience_information?.detailed_audience_stats
  ])

  const ageChartOption = useMemo(() => {
    const ages = currentAuthentication.audience_information?.detailed_audience_stats?.ages
    const genderByAge = currentAuthentication.audience_information?.detailed_audience_stats?.gender_by_age

    return {
      xAxis: {
        data: [
          `13-17`,
          `18-24`,
          `25-34`,
          `35-44`,
          `45-64`,
          `65+`,
          // `13-17${genderByAge ? `\n\n${(genderByAge?.[`13-17`]?.male * 100 || 0).toFixed(1)}% M\n${(genderByAge?.[`13-17`]?.female * 100 || 0).toFixed(1)}% F` : ''}`,
          // `18-24${genderByAge ? `\n\n${(genderByAge?.[`18-24`]?.male * 100 || 0).toFixed(1)}% M\n${(genderByAge?.[`18-24`]?.female * 100 || 0).toFixed(1)}% F` : ''}`,
          // `25-34${genderByAge ? `\n\n${(genderByAge?.[`25-34`]?.male * 100 || 0).toFixed(1)}% M\n${(genderByAge?.[`25-34`]?.female * 100 || 0).toFixed(1)}% F` : ''}`,
          // `35-44${genderByAge ? `\n\n${(genderByAge?.[`35-44`]?.male * 100 || 0).toFixed(1)}% M\n${(genderByAge?.[`35-44`]?.female * 100 || 0).toFixed(1)}% F` : ''}`,
          // `45-64${genderByAge ? `\n\n${(genderByAge?.[`45-64`]?.male * 100 || 0).toFixed(1)}% M\n${(genderByAge?.[`45-64`]?.female * 100 || 0).toFixed(1)}% F` : ''}`,
          // `65+${genderByAge ? `\n\n${(genderByAge?.[`65+`]?.male * 100 || 0).toFixed(1)}% M\n${(genderByAge?.[`65+`]?.female * 100 || 0).toFixed(1)}% F` : ''}`
        ],
        axisLabel: {
          interval: 0,
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
      },
      yAxis: {
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false }
      },
      series: [
        {
          type: 'bar',
          z: 0,
          barWidth: '60%',
          data: ['13-17', '18-24', '25-34', '35-44', '45-64', '65+'].map(ageGroup => ({
            value: +(ages?.[ageGroup] * 100).toFixed(2),
            label: { show: true, position: 'top', formatter: '{c}%' },
            itemStyle: {
              barBorderRadius: [10, 10, 0, 0],
              borderWidth: 1,
              borderType: 'solid',
              borderColor: '#2CCDB4',
              color: '#2CCDB4',
            }
          }))
        },

      ],
      grid: {
        left: 20,
        right: 20,
        top: 30,
        bottom: '0%',
        containLabel: true
      },
      textStyle: {
        fontSize: 14
      },
    }
  }, [currentAuthentication.audience_information?.detailed_audience_stats])

  const exportToPowerPoint = async () => {
    try {
      const pptx = new PptxGenJS()
      pptx.layout = "LAYOUT_WIDE";

      const slide = pptx.addSlide()

      // 1) Header: name (title) + description as editable text boxes
      const titleText = influencer?.full_name || currentAuthentication?.username || ''
      const descriptionText = currentAuthentication?.description || ''


        // Always add some content to ensure slide is not empty
        slide.addShape('rect', {
          x: 0,
          y: 0,
          w: 6.3,
          h: 7.5,
          fill: { color: 'F1F1F1' }
        })

      // 2) Posts: a kiválasztott képek (displayedPosts) 2x2 rácsban
      const postImages = (displayedPosts || []).slice(0, 4)
      for (let i = 0; i < postImages.length; i++) {
        const post = postImages[i]
        const src = post?.image?.url || post?.thumbnail?.url
        if (!src) continue

        try {
          // Elhelyezés: 2x2 rács, 0.2in margó, 0.2in hézag, képméret 3x3in
          const gap = 0.2
          const imgW = 2.8
          const imgH = 3.2
          const col = i % 2
          const row = Math.floor(i / 2)
          const x = gap + col * (imgW + gap)
          const y = gap + row * (imgH + gap)

          // Proxy-n keresztül töltjük be a képet, "cover" illesztéssel (nincs fehér szegély)
          const encoded = encodeURIComponent(src)
          // Kis ráhagyás az esetleges fehér szélek elkerülésére
          const imgBleed = 0.02
          slide.addImage({
            path: `/proxy/image?url=${encoded}`,
            x: x - imgBleed,
            y: y - imgBleed,
            w: imgW + imgBleed * 2,
            h: imgH + imgBleed * 2
          })
        } catch (e) {

        }
      }

      if (titleText) {
        slide.addText(titleText, {
          x: 6.5, y: 0.5, w: 6, h: 0.75 ,
          fontSize: 32, bold: true, color: '32414C', fontFace: 'Arial Black'
        })
      }

      if (descriptionText) {
        slide.addText(descriptionText, {
          x: 6.5, y: 1.5, w: 6, h: 2,
          fontSize: 9, color: '32414C', fontFace: 'Arial'
        })
      }
      // 3) Table as text box (similar to modal appearance)
      const providerLabel = (() => {
        switch ((currentAuthentication.provider || '').toLowerCase()) {
          case 'instagram': return 'Instagram'
          case 'tiktok': return 'TikTok'
          case 'youtube': return 'YouTube'
          default: return currentAuthentication.provider || ''
        }
      })()
      const tableLines = [['Channel:', providerLabel],
        ['Follower:', currentMetrics.reach || ''],
        ['Engagement rate:', currentMetrics.engagement || ''],
        ['German-share:', currentMetrics.german || '']
      ]
      slide.addTable(tableLines, {
        x: 7.5, y: 3.5, w: 4, h: 1.5,
        border: { color: 'ffffff', width: 4 },
        fontSize: 11,
        bold: true,
        align: 'left',
        color: '32414C',
        fontFace: 'Arial Black',
        fill: { color: 'F1F1F1' },
        valign: 'middle',
        align: 'center'
      })
      // 4) Chart using addChart with data from ageChartOption
      const ages = currentAuthentication?.audience_information?.detailed_audience_stats?.ages

      if (ages && typeof ages === 'object') {
        try {
          const chartData = [{
            name: "Age Distribution",
            labels: ['13-17', '18-24', '25-34', '35-44', '45-64', '65+'],
            values: ['13-17', '18-24', '25-34', '35-44', '45-64', '65+'].map(ageGroup => {
              const value = ages[ageGroup]
              return value ? +(value * 100).toFixed(2) : 0
            })
          }]


          slide.addChart('bar', chartData, {
            x: 6.5,
            y: 5.5,
            w: 6,
            h: 2,
            chartColors: ['#2CCDB4'],
            showTitle: false,
            showLegend: true,
            showValue: true,
            valGridLine: { style: 'none' }
          })
        } catch (chartError) {

        }
      } else {

      }

      // Generate
      const fileName = `sedcard_${influencer.full_name || currentAuthentication.username || 'unknown'}_${new Date().toISOString().split('T')[0]}.pptx`
      await pptx.writeFile({ fileName })

    } catch (error) {

      alert('Error occurred during PowerPoint export: ' + error.message)
    }
  }

  return (
    <>
    {!isCarouselOpen && (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Body>
        <div className="grid grid-cols-2 gap-6" style={{ width: '75vw' }}>
          <div className="col-span-1">
            <div ref={postsRef} className="grid grid-cols-2 grid-rows-2 gap-2">
              {displayedPosts.map((post, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => openCarouselForTile(idx)}
                  className="relative bg-gray-100 rounded-md overflow-hidden focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  style={{ width: '300px', height: '300px' }}
               >
                  {post?.thumbnail?.url ? (
                    <img className="w-full h-full object-cover" src={post.thumbnail.url} alt={`post-${idx}`} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">Select image</div>
                  )}
                </button>
              ))}
            </div>
          </div>
          <div className="col-span-1">
            <div ref={headerRef} className="mb-4">
              <p className="text-2xl font-extrabold">{influencer.full_name || currentAuthentication.username}</p>
              <p className="text-sm text-gray-700 mt-1">{currentAuthentication.description}</p>
            </div>
                <div ref={tableRef} className="overflow-auto mt-4 flex justify-center">
                  <table className="w-auto text-left border-2 border-white rounded-md" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                    <tbody>
                      <tr className="bg-gray-50">
                        <td className="px-3 py-2 font-semibold border-r-2 border-white" style={{ width: '240px' }}>Channel</td>
                        <td className="px-3 py-2 border-r-2 border-white text-center align-middle" style={{ width: '200px' }}>
                          <div className="flex items-center justify-center">
                            {currentAuthentication.provider === 'instagram' && <Instagram className="text-pink-500 text-lg"/>}
                            {currentAuthentication.provider === 'tiktok' && <Tiktok className="text-lg"/>}
                            {currentAuthentication.provider === 'youtube' && <Youtube className="text-lg text-[#F03B3B]"/>}
                          </div>
                        </td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="px-3 py-2 font-semibold border-r-2 border-white">Follower</td>
                        <td className="px-3 py-2 border-r-2 border-white text-center">{currentMetrics.reach || '-'}</td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="px-3 py-2 font-semibold border-r-2 border-white">Engagement rate</td>
                        <td className="px-3 py-2 border-r-2 border-white text-center">{currentMetrics.engagement || '-'}</td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="px-3 py-2 font-semibold border-r-2 border-white">German-share</td>
                        <td className="px-3 py-2 text-center">{currentMetrics.german || '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
            <div ref={chartRef} className="mt-0">
              <div className="w-full bg-white p-0 rounded-lg">
                <EChartsReact option={ageChartOption} style={{ width: '80%', height: '169px' }} />
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end w-full gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300">Close</button>
          <button
            onClick={exportToPowerPoint}
            className="px-4 py-2 rounded-md bg-rb-green text-white hover:bg-rb-green-dark"
          >
            Export to PowerPoint
          </button>
        </div>
      </Modal.Footer>
    </Modal>
    )}
    {isCarouselOpen && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-80">
        <div className="relative w-full h-full flex items-center justify-center">
          <button
            type="button"
            onClick={closeCarousel}
            className="absolute top-4 right-4 px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded"
          >Close</button>

          <button
            type="button"
            onClick={showPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 px-3 py-2 bg-white/80 hover:bg-white rounded"
          >‹</button>

          <div className="max-w-5xl w-[80vw] h-[80vh] relative">
            <div
              className="w-full h-full bg-black overflow-auto"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {allPosts[carouselIndex]?.image?.url || allPosts[carouselIndex]?.thumbnail?.url ? (
                <img
                  src={allPosts[carouselIndex]?.image?.url || allPosts[carouselIndex]?.thumbnail?.url}
                  alt={`carousel-${carouselIndex}`}
                  className="w-full h-auto object-contain"
                />
              ) : (
                <div className="text-white h-full flex items-center justify-center">No image</div>
              )}
            </div>
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-2 bg-black/50 px-4 py-2 rounded-lg">
              <button
                type="button"
                onClick={selectCurrentImageForTile}
                className="px-4 py-2 rounded-md bg-rb-green text-white hover:bg-rb-green-dark"
                >Select image</button>
              <span className="text-white text-sm">{carouselIndex + 1} / {allPosts.length}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={showNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 px-3 py-2 bg-white/80 hover:bg-white rounded"
          >›</button>
        </div>
      </div>
    )}
    </>
  )
})


